import { markChunkUploaded } from "@/lib/chunkDb"
import type { API } from "@/api/client"

const BASE_DELAY_MS = 1000
const MAX_DELAY_MS = 15000
const MAX_ATTEMPTS_PER_CHUNK = 8

class SessionClosedError extends Error {
    readonly chunkIndex: number
    constructor(chunkIndex: number) {
        super(`Chunk ${chunkIndex} rejected: recording session closed`)
        this.name = "SessionClosedError"
        this.chunkIndex = chunkIndex
    }
}

class QuotaExceededError extends Error {
    readonly chunkIndex: number
    readonly scope?: string
    constructor(chunkIndex: number, scope?: string) {
        super("demo quota exceeded")
        this.name = "QuotaExceededError"
        this.chunkIndex = chunkIndex
        this.scope = scope
    }
}

class PermanentUploadError extends Error {
    readonly chunkIndex: number
    readonly status?: number
    constructor(chunkIndex: number, status?: number) {
        super(`Chunk ${chunkIndex} permanently failed${status ? ` (status ${status})` : ""}`)
        this.name = "PermanentUploadError"
        this.chunkIndex = chunkIndex
        this.status = status
    }
}

function statusOf(err: unknown): number | undefined {
    return (
        (err as { status?: number })?.status ??
        (err as { response?: { status?: number } })?.response?.status
    )
}

function demoQuotaOf(err: unknown): { hit: boolean; scope?: string } {
    const data = (err as { response?: { data?: { code?: string; scope?: string } } })?.response?.data
    if (statusOf(err) !== 429 || data?.code !== "demo_quota_exceeded") return { hit: false }
    return { hit: true, scope: data.scope }
}

export function isRetryableStatus(status: number | undefined): boolean {
    if (status === undefined) return true
    if (status === 408 || status === 429) return true
    return status >= 500
}

export function computeBackoff(attempt: number, rand: number): number {
    const exp = Math.min(MAX_DELAY_MS, BASE_DELAY_MS * 2 ** attempt)
    return Math.round(exp / 2 + rand * (exp / 2))
}

interface QueueItem {
    chunkIndex: number
    blob: Blob
}

interface UploadQueueCallbacks {
    onChunkUploaded?: (chunkIndex: number) => void
    onDrained?: () => void
    onDeadLetter?: (chunkIndex: number) => void
    onSessionClosed?: (chunkIndex: number) => void
    onQuotaExceeded?: (scope?: string) => void
}

interface UploadQueueDeps {
    sleep?: (ms: number) => Promise<void>
    random?: () => number
    isOnline?: () => boolean
    waitForOnline?: () => Promise<void>
}

function defaultIsOnline(): boolean {
    return typeof navigator === "undefined" || navigator.onLine
}

export interface UploadQueue {
    enqueue: (chunkIndex: number, blob: Blob) => void
    resume: () => void
    pending: () => number
    hasDeadLetter: () => boolean
    isStopped: () => boolean
    stop: () => void
}

export function createUploadQueue(
    api: API,
    sessionId: string,
    getCopilotSessionId: () => string | undefined,
    cb: UploadQueueCallbacks = {},
    deps: UploadQueueDeps = {},
): UploadQueue {
    const sleep = deps.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)))
    const random = deps.random ?? Math.random
    const isOnline = deps.isOnline ?? defaultIsOnline

    const items: QueueItem[] = []
    let running = false
    let stopped = false
    let deadLettered = false
    let releaseOnlineWait: (() => void) | null = null

    function gateOnline(): Promise<void> {
        if (stopped || isOnline()) return Promise.resolve()
        if (deps.waitForOnline) return deps.waitForOnline()
        return new Promise<void>((resolve) => {
            const done = () => {
                window.removeEventListener("online", done)
                releaseOnlineWait = null
                resolve()
            }
            releaseOnlineWait = done
            window.addEventListener("online", done)
        })
    }

    async function uploadOne(item: QueueItem): Promise<void> {
        for (let attempt = 0; ; attempt++) {
            if (stopped) return
            await gateOnline()
            if (stopped) return
            try {
                await api.v1.uploadPart(sessionId, item.chunkIndex, item.blob as File, {
                    copilot_session_id: getCopilotSessionId(),
                })
                try {
                    await markChunkUploaded(sessionId, item.chunkIndex)
                } catch {
                    void 0
                }
                return
            } catch (err) {
                const status = statusOf(err)
                const quota = demoQuotaOf(err)
                if (quota.hit) throw new QuotaExceededError(item.chunkIndex, quota.scope)
                if (status === 409) throw new SessionClosedError(item.chunkIndex)
                if (!isRetryableStatus(status)) throw new PermanentUploadError(item.chunkIndex, status)
                if (attempt + 1 >= MAX_ATTEMPTS_PER_CHUNK) {
                    throw new PermanentUploadError(item.chunkIndex, status)
                }
                await sleep(computeBackoff(attempt, random()))
            }
        }
    }

    async function pump(): Promise<void> {
        if (running) return
        running = true
        try {
            while (items.length > 0 && !stopped) {
                const item = items[0]
                try {
                    await uploadOne(item)
                    if (stopped) return
                    items.shift()
                    cb.onChunkUploaded?.(item.chunkIndex)
                } catch (err) {
                    if (err instanceof SessionClosedError) {
                        items.length = 0
                        stopped = true
                        cb.onSessionClosed?.(err.chunkIndex)
                        return
                    }
                    if (err instanceof QuotaExceededError) {
                        items.length = 0
                        stopped = true
                        cb.onQuotaExceeded?.(err.scope)
                        return
                    }
                    deadLettered = true
                    items.shift()
                    cb.onDeadLetter?.(item.chunkIndex)
                }
            }
            if (!stopped && items.length === 0 && !deadLettered) cb.onDrained?.()
        } finally {
            running = false
        }
    }

    return {
        enqueue(chunkIndex, blob) {
            if (stopped) return
            items.push({ chunkIndex, blob })
            void pump()
        },
        resume() {
            void pump()
        },
        pending() {
            return items.length
        },
        hasDeadLetter() {
            return deadLettered
        },
        isStopped() {
            return stopped
        },
        stop() {
            stopped = true
            items.length = 0
            releaseOnlineWait?.()
        },
    }
}
